import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { RiskTemplateTagRow } from "./admin-risk-templates.js";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { LanguageSchema } from "./column-schemas.js";
import { CalendarDateFromString, InstantFromString } from "./temporal.js";
/**
 * `measure_templates` is the same kind of shared catalog as `risk_templates`
 * — no `client_id`, gated to the internal safety-catalog roles. Its tags
 * link table (`measure_template_tag_links`) shares `risk_template_tags` as
 * the catalog it references.
 */
export const MeasureTemplateRecurrence = S.Literal("weekly", "monthly", "quarterly", "half_yearly", "yearly", "two_yearly");
export const MeasureTemplateDueDateInterval = S.Literal("one_week", "two_weeks", "one_month", "three_months", "six_months", "one_year");
export const MeasureTemplatePriority = S.Literal("low", "medium", "high");
export const MeasureTemplateCategory = S.Literal("safety", "health");
export const MeasureTemplateRiskLinkRow = S.Struct({
    id: S.UUID,
    title: S.NullOr(S.String),
});
export const MeasureTemplateAttachmentRow = S.Struct({
    id: S.UUID,
    filename: S.NullOr(S.String),
});
export const MeasureTemplateRow = S.Struct({
    id: S.UUID,
    language: LanguageSchema,
    translation_group_id: S.NullOr(S.Int),
    title: S.String,
    description: S.NullOr(S.String),
    recurring: S.Boolean,
    recurrence: S.NullOr(MeasureTemplateRecurrence),
    due_date_interval: S.NullOr(MeasureTemplateDueDateInterval),
    due_date: S.NullOr(CalendarDateFromString),
    priority: MeasureTemplatePriority,
    category: MeasureTemplateCategory,
    auto_reminder: S.Boolean,
    auto_assign_new_employees: S.Boolean,
    risk_template_ids: S.Array(S.UUID),
    risk_templates: S.Array(MeasureTemplateRiskLinkRow),
    tags: S.Array(RiskTemplateTagRow),
    attachments: S.Array(MeasureTemplateAttachmentRow),
    linked_risks: S.Int,
    date_created: S.NullOr(InstantFromString),
    date_updated: S.NullOr(InstantFromString),
});
export const MeasureTemplateNotFound = S.Struct({
    _tag: S.Literal("MeasureTemplateNotFound"),
});
/** `recurring` must carry both `recurrence` and `due_date_interval`, or neither — the DB's `check_recurrence_consistency` constraint. */
export const InvalidRecurrenceConfig = S.Struct({
    _tag: S.Literal("InvalidRecurrenceConfig"),
});
export const ListMeasureTemplatesParams = S.extend(PagingParams, S.Struct({
    language: S.optional(LanguageSchema),
    category: S.optional(MeasureTemplateCategory),
    search: S.optional(S.String),
    translation_group_id: S.optional(S.NumberFromString.pipe(S.int())),
}));
export const ListMeasureTemplates = HttpApiEndpoint.get("listMeasureTemplates", "/admin/measure-templates")
    .setUrlParams(ListMeasureTemplatesParams)
    .addSuccess(pagedResponse(MeasureTemplateRow))
    .addError(Forbidden, { status: 403 });
export const ListMeasureTemplatesByIdsPayload = S.Struct({
    ids: S.Array(S.UUID),
});
/**
 * Bulk-by-id read for the offline inspection sync engine's cache refresh:
 * the measure templates linked to a batch of cached risk templates, keyed
 * by their own row id (not a translation group — `risk_template_measure_
 * links.measure_template_id` references `measure_templates.id` directly).
 * Ids ride the POST body since the linked set can exceed a URL length
 * limit.
 */
export const ListMeasureTemplatesByIds = HttpApiEndpoint.post("listMeasureTemplatesByIds", "/admin/measure-templates/by-ids")
    .setPayload(ListMeasureTemplatesByIdsPayload)
    .addSuccess(S.Struct({ items: S.Array(MeasureTemplateRow) }))
    .addError(Forbidden, { status: 403 });
export const GetMeasureTemplateParams = S.Struct({ id: S.UUID });
export const GetMeasureTemplate = HttpApiEndpoint.get("getMeasureTemplate", "/admin/measure-templates/get")
    .setUrlParams(GetMeasureTemplateParams)
    .addSuccess(S.Struct({ data: MeasureTemplateRow }))
    .addError(MeasureTemplateNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
const recurrenceFields = {
    recurring: S.optionalWith(S.Boolean, { default: () => false }),
    recurrence: S.optional(MeasureTemplateRecurrence),
    due_date_interval: S.optional(MeasureTemplateDueDateInterval),
};
export const CreateMeasureTemplatePayload = S.Struct({
    language: LanguageSchema,
    title: S.NonEmptyString,
    description: S.optional(S.String),
    translation_group_id: S.optional(S.Int),
    due_date: S.optional(CalendarDateFromString),
    priority: MeasureTemplatePriority,
    category: MeasureTemplateCategory,
    auto_reminder: S.optionalWith(S.Boolean, { default: () => true }),
    auto_assign_new_employees: S.optionalWith(S.Boolean, {
        default: () => true,
    }),
    ...recurrenceFields,
});
export const CreateMeasureTemplate = HttpApiEndpoint.post("createMeasureTemplate", "/admin/measure-templates/create")
    .setPayload(CreateMeasureTemplatePayload)
    .addSuccess(S.Struct({ data: MeasureTemplateRow }))
    .addError(InvalidRecurrenceConfig, { status: 400 })
    .addError(Forbidden, { status: 403 });
export const UpdateMeasureTemplatePayload = S.Struct({
    id: S.UUID,
    title: S.optional(S.NonEmptyString),
    description: S.optional(S.NullOr(S.String)),
    due_date: S.optional(S.NullOr(CalendarDateFromString)),
    priority: S.optional(MeasureTemplatePriority),
    category: S.optional(MeasureTemplateCategory),
    auto_reminder: S.optional(S.Boolean),
    auto_assign_new_employees: S.optional(S.Boolean),
    recurring: S.optional(S.Boolean),
    recurrence: S.optional(S.NullOr(MeasureTemplateRecurrence)),
    due_date_interval: S.optional(S.NullOr(MeasureTemplateDueDateInterval)),
});
export const UpdateMeasureTemplate = HttpApiEndpoint.post("updateMeasureTemplate", "/admin/measure-templates/update")
    .setPayload(UpdateMeasureTemplatePayload)
    .addSuccess(S.Struct({ data: MeasureTemplateRow }))
    .addError(MeasureTemplateNotFound, { status: 404 })
    .addError(InvalidRecurrenceConfig, { status: 400 })
    .addError(Forbidden, { status: 403 });
export const DeleteMeasureTemplatePayload = S.Struct({ id: S.UUID });
export const DeleteMeasureTemplate = HttpApiEndpoint.post("deleteMeasureTemplate", "/admin/measure-templates/delete")
    .setPayload(DeleteMeasureTemplatePayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(MeasureTemplateNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const AddMeasureTemplateTagPayload = S.Struct({
    template_id: S.UUID,
    tag_group_id: S.Int,
});
export const AddMeasureTemplateTag = HttpApiEndpoint.post("addMeasureTemplateTag", "/admin/measure-templates/tags/add")
    .setPayload(AddMeasureTemplateTagPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.Int }) }))
    .addError(MeasureTemplateNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const RemoveMeasureTemplateTagPayload = S.Struct({
    template_id: S.UUID,
    tag_group_id: S.Int,
});
export const RemoveMeasureTemplateTag = HttpApiEndpoint.post("removeMeasureTemplateTag", "/admin/measure-templates/tags/remove")
    .setPayload(RemoveMeasureTemplateTagPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const AddMeasureTemplateAttachmentPayload = S.Struct({
    template_id: S.UUID,
    file_id: S.UUID,
});
export const AddMeasureTemplateAttachment = HttpApiEndpoint.post("addMeasureTemplateAttachment", "/admin/measure-templates/attachments/add")
    .setPayload(AddMeasureTemplateAttachmentPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.Int }) }))
    .addError(MeasureTemplateNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const RemoveMeasureTemplateAttachmentPayload = S.Struct({
    template_id: S.UUID,
    file_id: S.UUID,
});
export const RemoveMeasureTemplateAttachment = HttpApiEndpoint.post("removeMeasureTemplateAttachment", "/admin/measure-templates/attachments/remove")
    .setPayload(RemoveMeasureTemplateAttachmentPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const adminMeasureTemplatesGroup = HttpApiGroup.make("admin-measure-templates")
    .add(ListMeasureTemplates)
    .add(ListMeasureTemplatesByIds)
    .add(GetMeasureTemplate)
    .add(CreateMeasureTemplate)
    .add(UpdateMeasureTemplate)
    .add(DeleteMeasureTemplate)
    .add(AddMeasureTemplateTag)
    .add(RemoveMeasureTemplateTag)
    .add(AddMeasureTemplateAttachment)
    .add(RemoveMeasureTemplateAttachment)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-measure-templates.js.map